/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Formik, FormikValues, Form } from 'formik';
import moment from 'moment';
import * as yup from 'yup';

import { doReset, doSetGuest } from 'action-creators/travelArrangerGuest';
import Header from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Field from 'components/Field';
import Input from 'components/Input';
import ModalHeader from 'components/ModalHeader';
import MonthSelect from 'components/MonthSelect';
import { Mobile, Desktop } from 'components/Responsive';
import OnboardingSelect from 'components/Selects/OnboardingSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { NationalityCountry } from 'models/country';
import { Group } from 'models/group';
import { Guest } from 'services/userGuestService';

import GenderSelect from './components/GenderSelect';
import { Container, Content, ErrorValidation, StyledButton } from './styles';

interface Props {
  show: boolean;
  onSubmit: (guest: Guest) => void;
  countries: NationalityCountry[];
  loading: boolean;
  onClose: () => void;
  travelArrangerGuest: Guest;
  groups: Group[];
}

interface Data {
  id: string;
  name: string;
}

const GuestForm = ({ show, countries, onSubmit, loading, onClose, travelArrangerGuest, groups }: Props) => {
  const { t } = useStoreTranslation();
  const dispatch = useDispatch();
  const [month, setMonth] = useState<number>();

  const validationSchema = yup.object().shape({
    name: yup.string().trim().max(100).required(),
    lastName: yup.string().trim().max(100).required(),
    phoneNumber: yup.string().phone().required(),
    country: yup.string().required(),
    birthDate_day: yup.number().positive().moreThan(0).lessThan(32, t('guestForm.day.invalidNumber')).required(),
    birthDate_year: yup.number().moreThan(1910).lessThan(new Date().getFullYear() + 1, t('guestForm.year.invalidNumber')).required(),
    email: yup.string().email().required(),
    gender: yup.string().required(),
    documentNumber: yup.string().trim().max(100).required(),
    passportNumber: yup.string().trim().max(100),
    userGroupId: yup.string().required(),
  });

  //Initial Values para cuando empieza la pantalla
  const initValues: Guest = {
    name: travelArrangerGuest?.name || '',
    lastName: travelArrangerGuest?.lastName || '',
    phoneNumber: travelArrangerGuest?.phoneNumber || '',
    country: travelArrangerGuest?.country || '',
    birthDate: travelArrangerGuest?.birthDate || '',
    birthDate_day: moment(travelArrangerGuest?.birthDate)?.format('DD') || '',
    birthDate_month: moment(travelArrangerGuest?.birthDate)?.format('MM') || '',
    birthDate_year: moment(travelArrangerGuest?.birthDate)?.format('YYYY') || '',
    email: travelArrangerGuest?.email || '',
    gender: travelArrangerGuest?.gender || 'Male',
    documentNumber: travelArrangerGuest?.documentNumber || '',
    passportNumber: travelArrangerGuest?.passportNumber || '',
    userGroupId: travelArrangerGuest?.userGroupId || '',
  };

  //Busca el Guest del Redux
  useEffect(() => {
    initValues.name = travelArrangerGuest?.name || '';
    initValues.lastName = travelArrangerGuest?.lastName || '';
    initValues.phoneNumber = travelArrangerGuest?.phoneNumber || '';
    initValues.country = travelArrangerGuest?.country || '';
    initValues.birthDate = travelArrangerGuest?.birthDate || '';
    initValues.birthDate_day = moment(travelArrangerGuest?.birthDate)?.format('DD') || '';
    initValues.birthDate_year = moment(travelArrangerGuest?.birthDate)?.format('YYYY') || '';
    initValues.email = travelArrangerGuest?.email || '';
    // @ts-ignore
    initValues.gender = travelArrangerGuest?.gender || '';
    initValues.documentNumber = travelArrangerGuest?.documentNumber || '';
    initValues.passportNumber = travelArrangerGuest?.passportNumber || '';
    initValues.userGroupId = travelArrangerGuest?.userGroupId || '';
    // eslint-disable-next-line
  }, [travelArrangerGuest]);

  //Cuando cierra la pantalla, guarda las informaciones en Redux
  const handleOnClose = () => {
    //Gender
    let genderValue = 'Male';
    const genderNode = document.getElementsByClassName('input_gender_travelArranger') as HTMLCollection;

    if ((genderNode[0] as HTMLElement).innerText.indexOf(t('guestForm.genderValues.male')) !== -1) {
      genderValue = 'Male';
    } else {
      genderValue = 'Female';
    }

    //Countries
    const countryNode = document.getElementsByClassName('input_country_travelArranger') as HTMLCollection;
    const country = countries.find(function (c) {
      if ((countryNode[0] as HTMLElement).innerText.indexOf(c.name) !== -1) {
        return c;
      }
    });

    //UserGroup
    const groupNode = document.getElementsByClassName('input_group_travelArranger') as HTMLCollection;
    const group =
      groups !== undefined
        ? groups.find(function (c) {
          if ((groupNode[0] as HTMLElement).innerText.indexOf(c.name) !== -1) {
            return c;
          }
        })
        : null;

    const currentGuest: Guest = {
      name: (document.getElementById('input_name_travelArranger') as HTMLInputElement)?.value || '',
      lastName: (document.getElementById('input_lastName_travelArranger') as HTMLInputElement)?.value || '',
      phoneNumber: (document.getElementById('input_phoneNumber_travelArranger') as HTMLInputElement)?.value || '',
      country: country?.id || '',
      birthDate: (document.getElementById('input_birthDate_travelArranger') as HTMLInputElement)?.value || '',
      birthDate_day: (document.getElementById('input_birthDate_day_travelArranger') as HTMLInputElement)?.value || '',
      birthDate_month: (document.getElementById('custom_select_input_birthDate_month_travelArranger') as HTMLInputElement)?.value || '',
      birthDate_year: (document.getElementById('input_birthDate_year_travelArranger') as HTMLInputElement)?.value || '',
      email: (document.getElementById('input_email_travelArranger') as HTMLInputElement)?.value || '',
      gender: genderValue === 'Male' ? 'Male' : 'Female',
      documentNumber: (document.getElementById('input_documentNumber_travelArranger') as HTMLInputElement)?.value || '',
      passportNumber: (document.getElementById('input_passportNumber_travelArranger') as HTMLInputElement)?.value || '',
      userGroupId: group?.id || '',
    };

    dispatch(doSetGuest(currentGuest));
    onClose();
  };

  const handleGroups = (): Data[] => {
    if (groups !== undefined) {
      return groups?.map((group) => ({
        id: group.id,
        name: group.name,
      }))
    } else {
      return [];
    }
  };

  const handleOnSubmit = (guest) => {
    const formattedMonth = month && month < 10 ? '0' + month : month;
    dispatch(doReset());
    onSubmit({ ...guest, birthDate_month: formattedMonth });
  };

  const isDateOfBirthValid = () => {

    const birthDate_day = (document.getElementById('input_birthDate_day_travelArranger') as HTMLInputElement)?.value || '';
    const birthDate_year = (document.getElementById('input_birthDate_year_travelArranger') as HTMLInputElement)?.value || '';

    return moment(`${birthDate_year}-${month}-${birthDate_day}`, 'YYYY-MM-DD').isValid();
  };

  const handleChangeMonth = (month: number) => {
    setMonth(month);
  }

  return (
    <ApplicationOverlayContent
      width={700}
      sidebar
      show={show}
      onClose={() => {
        handleOnClose();
      }}
    >
      <Mobile>
        <Header
          noGoBack
          rightIcon="/corporate/images/close.svg"
          rightAction={() => {
            handleOnClose();
          }}
        >
          {t('guestForm.title')}
        </Header>
      </Mobile>

      <Desktop>
        <ModalHeader
          title={t('guestForm.title')}
          onClose={() => {
            handleOnClose();
          }}
        />
      </Desktop>

      <Container>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          onSubmit={(guest) => {
            handleOnSubmit(guest);
          }}
          isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
        >
          {({ isValid }: FormikValues) => (
            <Form>
              <Field name="name" label={t('guestForm.name')}>
                <Input id="input_name_travelArranger" placeholder={t('guestForm.namePlaceHolder')} />
              </Field>
              <Field name="lastName" label={t('guestForm.lastName')}>
                <Input id="input_lastName_travelArranger" placeholder={t('guestForm.lastNamePlaceHolder')} />
              </Field>
              <Content>
                <Field name="birthDate_day" label={t('guestForm.day.label')}>
                  <Input
                    type="text"
                    id="input_birthDate_day_travelArranger"
                    mask="99"
                    inputMode="numeric"
                    placeholder={t('guestForm.day.placeholder')}
                  />
                </Field>
                <Field name="birthDate_month">
                  <MonthSelect handleChangeDate={handleChangeMonth} name="input_birthDate_month_travelArranger" />
                </Field>
                <Field name="birthDate_year" label={t('guestForm.year.label')}>
                  <Input
                    type="text"
                    id="input_birthDate_year_travelArranger"
                    mask="9999"
                    inputMode="numeric"
                    placeholder={t('guestForm.year.placeholder')}
                  />
                </Field>
                {!isDateOfBirthValid() && (
                  <>
                    <ErrorValidation>
                      {t('guestForm.dateOfBirthInvalid')}
                    </ErrorValidation>
                  </>
                )}
              </Content>
              <Field className="input_gender_travelArranger" name="gender" label={t('guestForm.gender')}>
                <GenderSelect />
              </Field>
              <Field name="phoneNumber" label={t('guestForm.phoneNumber')}>
                <Input
                  id="input_phoneNumber_travelArranger"
                  type="text"
                  inputMode="tel"
                  placeholder={t('guestForm.phoneNumberPlaceHolder')}
                />
              </Field>
              <Field name="email" label={t('guestForm.email')}>
                <Input id="input_email_travelArranger" placeholder={t('guestForm.emailPlaceHolder')} />
              </Field>
              <Field name="documentNumber" label={t('guestForm.documentNumber')}>
                <Input 
                  id="input_documentNumber_travelArranger" 
                  placeholder={t('guestForm.documentPlaceHolder')} 
                  onChange={(e) => {
                    e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
                  }} />
              </Field>
              <Field name="passportNumber" label={t('guestForm.passportNumber')}>
                <Input 
                  id="input_passportNumber_travelArranger" 
                  placeholder={t('guestForm.documentPlaceHolder')} 
                  onChange={(e) => {
                    e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
                  }} />
              </Field>
              <Field className="input_country_travelArranger" name="country" label={t('guestForm.country')}>
                <OnboardingSelect
                  enableSearch={true}
                  type="nationality"
                  options={countries}
                  title={t('guestForm.countrySelect')}
                />
              </Field>
              <Field className="input_group_travelArranger" name="userGroupId" label={t('guestForm.group')}>
                <OnboardingSelect type="userGroupId" options={handleGroups()} title={t('guestForm.groupSelect')} />
              </Field>
              <StyledButton loading={loading} disabled={!isValid || loading || !isDateOfBirthValid()} value={t('guestForm.buttonValue')} />
            </Form>
          )}
        </Formik>
      </Container>
    </ApplicationOverlayContent>
  );
};

export default GuestForm;
